import {
  REPAYMENT_DATA_SOURCE_OPTIONS,
  REPAYMENT_DESCRIPTION_OPTIONS,
} from "@/domain/repayment-options";
import { QueryParamsService } from "@/service/query-params.service";
import { RepaymentsService } from "@/service/repayments.service";
import { UploadedFileService } from "@/service/uploaded-file.service";
import { SyncAuditService } from "@/service/sync-audit.service";
import { OutboundSyncsService } from "@/service/outbound-syncs.service";
import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterModule,
} from "@angular/router";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { MenubarModule } from "primeng/menubar";
import { TabList, TabsModule } from "primeng/tabs";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";
import { Subject, filter, takeUntil } from "rxjs";
import * as XLSX from "xlsx";
import { ThemeSwitcher } from "../theme-switcher/theme-switcher.component";
import { UploadFileDialogComponent } from "../upload-file/upload-file-dialog.component";

@Component({
  selector: "app-main",
  imports: [
    CommonModule,
    MenubarModule,
    RouterModule,
    ThemeSwitcher,
    UploadFileDialogComponent,
    ToastModule,
    TabsModule,
    TooltipModule,
  ],
  templateUrl: "./main.component.html",
  styleUrl: "./main.component.scss",
  providers: [MessageService, ConfirmationService],
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
  items: MenuItem[] | undefined;
  visible: boolean = false;
  showAll: boolean;
  country: string | null = null;
  sourceFileId: string | null = null;
  currentPage: string;

  visibleTabs;

  private readonly destroy$ = new Subject<void>();
  private readonly timeouts = new Set<ReturnType<typeof setTimeout>>();
  private readonly onWindowResize = () => this.updateTabInkBar();
  private quarantineCountTimer: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;

  tabs: any = [
    {
      route: "uploaded-files",
      queryParams: { sourceFileId: null, showAll: null },
      hideFor: ["ie"],
      label: "Uploaded Files",
      icon: "pi pi-folder",
    },
    {
      route: "repayments",
      queryParams: { sourceFileId: null, showAll: true },
      hideFor: null,
      label: "Repayments",
      icon: "pi pi-money-bill",
    },
    {
      route: "in-progress",
      queryParams: { sourceFileId: null, showAll: true },
      hideFor: null,
      label: "In Progress",
      icon: "pi pi-clock",
    },
    // {
    //   route: 'merchants',
    //   queryParams: { sourceFileId: null, showAll: true },
    //   label: 'Merchants',
    //   icon: 'pi pi-shop',
    // },

    // {
    //   route: 'terminals',
    //   queryParams: { sourceFileId: null, showAll: true },
    //   hideFor: null,
    //   label: 'Terminals',
    //   icon: 'pi pi-credit-card',
    // },
    // {
    //   route: 'cash-advances',
    //   queryParams: { sourceFileId: null, showAll: true },
    //   label: 'Cash Advances',
    //   icon: 'pi pi-pound',
    // },
    {
      route: "reports",
      queryParams: { sourceFileId: null, showAll: null },
      hideFor: null,
      label: "Reports",
      icon: "pi pi-chart-bar",
    },
    {
      route: "sync-audit",
      queryParams: { sourceFileId: null, showAll: null },
      hideFor: null,
      label: "Sync Audit",
      icon: "pi pi-desktop",
    },
    {
      route: "sync-quarantine",
      queryParams: { sourceFileId: null, showAll: null },
      hideFor: null,
      label: "Sync Quarantine",
      icon: "pi pi-exclamation-triangle",
    },
    {
      route: "pending-syncs",
      queryParams: { sourceFileId: null, showAll: null },
      hideFor: null,
      label: "Pending Syncs",
      icon: "pi pi-clock",
    },
    {
      route: "repayment-exports",
      queryParams: { sourceFileId: null, showAll: null },
      hideFor: null,
      label: "Exports",
      icon: "pi pi-file-export",
    },
  ];
  // PrimeNG's ink bar belongs to p-tablist, not the containing p-tabs element.
  // Query the actual component so it can be positioned after the initial route renders.
  @ViewChild(TabList) tabListComponent?: TabList;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly queryParamsService: QueryParamsService,
    private readonly repaymentsService: RepaymentsService,
    private readonly uploadedFileService: UploadedFileService,
    private readonly syncAuditService: SyncAuditService,
    private readonly outboundSyncsService: OutboundSyncsService,
  ) {}

  get outboundSyncStatus$() {
    return this.outboundSyncsService.status$;
  }

  entityTypeLabel(type: string): string {
    return type.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  ngOnInit() {
    this.outboundSyncsService.startStatusMonitor();
    this.syncAuditService.quarantineEntityCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        const quarantineTab = this.tabs.find((tab) => tab.route === 'sync-quarantine');
        if (quarantineTab) {
          quarantineTab.count = count;
          // The count changes this preceding tab's width after initial render.
          // Reposition the active-tab underline once Angular has painted it.
          this.scheduleTask(() => this.updateTabInkBar());
        }
      });
    void this.refreshQuarantineEntityCount();
    this.quarantineCountTimer = setInterval(
      () => void this.refreshQuarantineEntityCount(),
      10_000,
    );
    this.ensureDefaultQueryParams();
    this.queryParamsService.queryParamMap$
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (this.destroyed) {
          return;
        }
        this.country = params.get("country");
        this.sourceFileId = params.get("sourceFileId");
        const repaymentsTab = this.tabs.find(
          (tab) => tab.route === "repayments",
        );
        repaymentsTab.label = this.sourceFileId
          ? "Repayments for File"
          : "All Repayments";
        repaymentsTab.queryParams = {
          showAll: this.sourceFileId ? false : true,
        };

        this.visibleTabs = this.tabs.filter((tab) => {
          // Show tab if it has no hideFor or if the current country is not in hideFor
          return (
            !tab.hideFor ||
            (Array.isArray(tab.hideFor) && !tab.hideFor.includes(this.country))
          );
        });
        this.scheduleTask(() => {
          if (this.destroyed) {
            return;
          }
          this.updateTabInkBar();
          // this.changeDetector.detectChanges()
        });
      });
    const initialChildRoute = this.getDeepestChild(this.route);
    this.currentPage = initialChildRoute.snapshot.routeConfig?.path ?? "";
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (this.destroyed) {
          return;
        }
        this.cancelPendingRequests();
      });

    // Update currentPage whenever the route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (this.destroyed) {
          return;
        }
        const childRoute = this.getDeepestChild(this.route);
        this.currentPage = childRoute.snapshot.routeConfig?.path ?? "";
        this.ensureDefaultQueryParams();
        this.scheduleTask(() => this.updateTabInkBar());
      });
    this.items = [
      {
        label: "File",
        icon: "pi pi-file",
        items: [
          {
            label: "Upload New File",
            icon: "pi pi-plus",
            command: () => this.showDialog(),
          },
          {
            label: "Download Repayments Template",
            icon: "pi pi-download",
            command: () => this.downloadRepaymentsTemplate(),
          },
        ],
      },
      {
        label: "Region",
        icon: "pi pi-globe",
        items: [
          {
            label: "All",
            icon: "all pi pi-globe",
            command: () =>
              this.router.navigate([""], {
                queryParams: { country: "all" },
                queryParamsHandling: "merge", // optional: to keep existing query params
              }),
          },
          {
            label: "UK",
            icon: "flag flag-uk",
            command: () =>
              this.router.navigate([""], {
                queryParams: { country: "uk", sourceFileId: null },
                queryParamsHandling: "merge", // optional: to keep existing query params
              }),
          },
          {
            label: "Ireland",
            icon: "flag flag-ie",
            command: () =>
              this.router.navigate(["repayments"], {
                queryParams: {
                  country: "ie",
                  showAll: true,
                  sourceFileId: null,
                },
                queryParamsHandling: "merge", // optional: to keep existing query params
              }),
          },
        ],
      },
      {
        separator: true,
      },
    ];
  }

  ngAfterViewInit(): void {
    this.scheduleTask(() => this.updateTabInkBar());
    window.addEventListener('resize', this.onWindowResize);
    // PrimeIcons load asynchronously on a full refresh. Their final widths shift
    // the tab labels, so remeasure after the browser finishes loading fonts.
    document.fonts?.ready.then(() => this.updateTabInkBar());
  }

  private ensureDefaultQueryParams(): void {
    const snapshot = this.route.snapshot;
    const deepestRoute = this.getDeepestChild(this.route);
    const currentPath = deepestRoute.snapshot.routeConfig?.path;
    const nextQueryParams: Record<string, unknown> = {};

    const currentCountry = snapshot.queryParamMap.get("country");
    if (!currentCountry) {
      nextQueryParams["country"] = "uk";
    }

    const sourceFileId = snapshot.queryParamMap.get("sourceFileId");
    const showAll = snapshot.queryParamMap.get("showAll");

    // This option belongs only to the Reports view. Tab links merge query
    // parameters, so explicitly remove it when navigating elsewhere.
    if (currentPath !== "reports" && snapshot.queryParamMap.has("reportDays")) {
      nextQueryParams["reportDays"] = null;
    }

    const needsShowAllDefault =
      (currentPath === "repayments" || currentPath === "in-progress") &&
      !sourceFileId &&
      showAll === null;

    if (needsShowAllDefault) {
      nextQueryParams["showAll"] = true;
    }

    if (Object.keys(nextQueryParams).length === 0) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: nextQueryParams,
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  showDialog() {
    if (this.destroyed) {
      return;
    }
    this.visible = true;
  }

  downloadRepaymentsTemplate(): void {
    const columns = [
      "Repayment Date (dd/mm/yyyy)",
      "Description",
      "Amount",
      "Data Source",
      "365 Split",
      "Comments",
      "Terminal (name)",
      "Terminal (id)",
      "Cash Advance (name)",
      "Cash Advance (id)",
      "Customer Number",
    ];
    const worksheet = XLSX.utils.aoa_to_sheet([columns]);
    worksheet["!cols"] = columns.map((column) => ({
      wch: Math.max(column.length + 2, 16),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Repayments");
    const choices = XLSX.utils.aoa_to_sheet([
      ["Description", "Data Source"],
      ...Array.from(
        {
          length: Math.max(
            REPAYMENT_DESCRIPTION_OPTIONS.length,
            REPAYMENT_DATA_SOURCE_OPTIONS.length,
          ),
        },
        (_, index) => [
          REPAYMENT_DESCRIPTION_OPTIONS[index] ?? "",
          REPAYMENT_DATA_SOURCE_OPTIONS[index] ?? "",
        ],
      ),
    ]);
    choices["!cols"] = [{ wch: 24 }, { wch: 28 }];
    XLSX.utils.book_append_sheet(workbook, choices, "Allowed Values");
    XLSX.writeFile(workbook, "repayments-template.xlsx");
  }

  hideDialog() {
    if (this.destroyed) {
      return;
    }
    // this.uploadedFileService.loadAllFiles()
    this.visible = false;
  }

  onTabInteraction(targetRoute: string | number | undefined): void {
    if (this.destroyed) {
      return;
    }
    const normalizedValue =
      typeof targetRoute === "number"
        ? targetRoute.toString()
        : (targetRoute ?? null);

    if (normalizedValue === this.currentPage) {
      return;
    }

    this.cancelPendingRequests();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.clearScheduledTasks();
    window.removeEventListener('resize', this.onWindowResize);
    if (this.quarantineCountTimer) clearInterval(this.quarantineCountTimer);
    this.outboundSyncsService.stopStatusMonitor();
  }

  private cancelPendingRequests(): void {
    this.repaymentsService.cancelAllRequests();
    this.uploadedFileService.cancelAllRequests();
  }

  private async refreshQuarantineEntityCount(): Promise<void> {
    try {
      const deliveries = await this.syncAuditService.listAtomicDeliveries();
      if (!this.destroyed) {
        // The tab opens a recovery list with one row per delivery. Count those
        // rows rather than their nested entities so the badge matches the UI.
        this.syncAuditService.setQuarantineEntityCount(deliveries.length);
      }
    } catch {
      // Preserve the last known count while the authenticated proxy is unavailable.
    }
  }

  private scheduleTask(callback: () => void, delay = 0) {
    const timeout = setTimeout(() => {
      this.timeouts.delete(timeout);
      if (this.destroyed) {
        return;
      }
      callback();
    }, delay);
    this.timeouts.add(timeout);
  }

  private updateTabInkBar(): void {
    // Wait one frame so Angular has applied the selected tab's dimensions before
    // PrimeNG measures the underline position (especially on a direct refresh).
    requestAnimationFrame(() => {
      if (!this.destroyed) {
        this.tabListComponent?.updateInkBar();
      }
    });
  }

  private clearScheduledTasks() {
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts.clear();
  }
}
